import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSyncState } from 'src/hooks';
import InfoIcon from '@mui/icons-material/Info';
import { AlertType, useAlert } from 'src/components/AlertsProvider';
import ModalDialog, {
  ModalCloseHandler,
  ModalCloseReason,
} from 'src/components/ModalDialog';
import {
  generateApiPath,
  loadDataFromStorage,
  saveDataToStorage,
} from 'src/utils';
import { pepApiRequest, RequestMethod, useStatefulApi } from 'src/services';
import { useStoreUnitActions } from 'src/redux/helpers';
import { useMsal } from '@azure/msal-react';
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  PublicClientApplication,
} from '@azure/msal-browser';

import { SxProps } from 'src/theme/types';
import { useForm } from 'src/components/Form';
import Button from '@mui/material/Button';
import { Grid, Typography } from '@mui/material';
import { useAppConfig } from 'src/config';
import solutionsStore, {
  useCurrentSolutionName,
  useDashboardDeviceGroupsOptions,
  useSolutionCompaniesOptions,
  useSolutionDashboardDeviceGroupsDetails,
  useSolutionDeviceGroupPropOptions,
  useSolutionDeviceGroupProperties,
  useSolutionDeviceTemplateOptions,
  useSolutionRuleTelemtryProperties,
  useSolutionTelemtryGroupPropOptions,
} from '../../redux';
import { API_SOLUTION_SAVE_DASHBOARD } from '../../constants';
import { loginRequest } from '../../SolutionDashboards/components/msalConfig';
import { msalInstanceManager } from '../../SolutionDashboards/components/msalInstanceManager';

type Props = {
  id: string;
  open: boolean;
  onClose: ModalCloseHandler;
};

const formStyle: SxProps = {
  gap: 0.5,
  '.MuiFormControlLabel-root': {
    alignSelf: 'flex-end',
    color: 'neutral.dark',
    typography: 'small1',
    marginTop: '20px',
  },
};

function SolutionAssetDashboardCreateContainer({ id, open, onClose }: Props) {
  const {
    clusterDetails,
    clusterZone,
    msalTenantId,
    msalClientId,
    pepsenseUrl,
  } = useAppConfig();
  const msalInstance = msalInstanceManager.getInstance(
    msalTenantId,
    msalClientId,
    pepsenseUrl
  );
  const iframeRef = useRef(null);
  const { inProgress, instance } = useMsal();
  const [dashboardToken, setDashboardToken] = useState<string>();
  const actions = useStoreUnitActions(solutionsStore);
  const { showAlert } = useAlert();
  const { solutionId } = useParams();
  const solutionName = useCurrentSolutionName();
  const [name, setName] = useState();
  const [functionOrMaterializedView, setFunctionOrMaterializedView] =
    useState('-');
  const msalTokenData = loadDataFromStorage('msalToken');
  const accountDetails = loadDataFromStorage('msalAccountDetail');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>();
  const [iframeLoad, setIframeLoad] = useState(false);
  const onNameChange = (event) => {
    setName(event.target.value);
  };

  const iframeConfig = {
    cluster: clusterDetails,
    zone: clusterZone,
    features:
      'f-IFrameAuth=true&f-UseMeControl=true&f-ShowNavigation=true&f-ShowPersona=true&f-Homepage=false',
  };

  const handleLogin = async () => {
    try {
      await msalInstance.initialize();
      await msalInstance
        .loginPopup(loginRequest)
        .then((response) => {
          if (response?.account) {
            setIframeLoad(true);
            setUser(response?.account);
            saveDataToStorage('msalExpireTime', response?.expiresOn?.getTime());
            saveDataToStorage('msalToken', response.idToken);
            saveDataToStorage('msalAccountDetail', response?.account);
          }
        })
        .catch((error) => {
          console.error('Login error', error);
        });
      setIsAuthenticated(true);
    } catch (error: any) {
      if (error.errorCode === 'interaction_in_progress') {
        console.warn('Another interaction is in progress. Please wait.');
      } else {
        console.error('Login error:', error);
      }
    }
  };

  const isTokenExpired = () => {
    const msalExpireTime = loadDataFromStorage('msalExpireTime');
    return msalExpireTime && new Date().getTime() > msalExpireTime;
  };

  const refreshTokenSilently = async () => {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const account = accounts[0];
        msalInstance.setActiveAccount(account);
        const silentRequest = {
          scopes: ['user.read'],
          account,
        };
        const response = await msalInstance.acquireTokenSilent(silentRequest);
        saveDataToStorage('msalToken', response.accessToken);
        setUser(account);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Silent token refresh error:', error);
      handleLogin(); // If silent refresh fails, prompt re-authentication
    }
  };

  const checkTokenValidity = async () => {
    if (isTokenExpired()) {
      await refreshTokenSilently();
    }
  };

  function mapScope(scope) {
    switch (scope) {
      case 'query':
        return [
          `https://${iframeConfig.cluster}.${iframeConfig.zone}.kusto.windows.net/.default`,
        ];
      case 'People.Read':
        return ['People.Read', 'User.ReadBasic.All', 'Group.Read.All'];
      case 'https://rtd-metadata.azurewebsites.net/user_impersonation':
        return ['https://rtd-metadata.azurewebsites.net/user_impersonation'];
      default:
        return [scope];
    }
  }
  const postToken = (iframe, accessToken, scope) => {
    iframe.contentWindow.postMessage(
      {
        type: 'postToken',
        message: accessToken,
        scope,
      },
      '*'
    );
  };

  useEffect(() => {
    if (!msalTokenData) {
      handleLogin();
    } else {
      setIframeLoad(true);
    }
  }, []);

  useEffect(() => {
    if (user) {
      setIframeLoad(true);
    }
  }, [user, msalTokenData]);

  useEffect(() => {
    checkTokenValidity();
  }, []);

  useEffect(() => {
    const handleIframeMessage = async (event) => {
      if (
        event.data.type !== 'getToken' ||
        event.origin !== 'https://dataexplorer.azure.com'
      ) {
        return;
      }
      const aadScopes = mapScope(event.data.scope);
      const iframe = iframeRef.current;
      const accountsDetails = [accountDetails];
      if (accountsDetails.length > 0 && inProgress === InteractionStatus.None) {
        await msalInstance.initialize();
        await msalInstance.handleRedirectPromise();
        msalInstance
          .acquireTokenSilent({
            scopes: aadScopes,
            account: accountsDetails[0],
          })
          .then((response) => {
            setDashboardToken(response.accessToken);
            postToken(iframe, response.accessToken, event.data.scope);
          })
          .catch((error) => {
            if (error instanceof InteractionRequiredAuthError) {
              msalInstance
                .acquireTokenPopup({ scopes: aadScopes })
                .then((response) =>
                  postToken(iframe, response.accessToken, event.data.scope)
                )
                .catch((error) => {
                  console.error(`Error acquiring token: ${error}`);
                });
            } else {
              console.error(`Error acquiring token: ${error}`);
            }
          });
      }
    };
    // Attach event listener when the component mounts
    window.addEventListener('message', handleIframeMessage);
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [accountDetails]);

  // const [handleAnalyse, saving] = useStatefulApi(
  //   () => {
  //     if (!loadDataFromStorage('msalToken')) {
  //       handleLogin();
  //     }
  //     return pepApiRequest({
  //       method: RequestMethod.Post,
  //       url: generateApiPath(API_SOLUTION_ANALYSE_DASHBOARD, { solutionId }),
  //       params: getPayloadParams,
  //     });
  //   },
  //   (respone) => {
  //     setFunctionOrMaterializedView(respone.results[0].name);
  //     showAlert({
  //       type: AlertType.Success,
  //       title: 'Analyse created and saved',
  //       text: `Analyse for the  ${solutionName} is created successfully`,
  //     });
  //   }
  // );

  const [handleSubmit, loading] = useStatefulApi(
    () => {
      if (!loadDataFromStorage('msalToken')) {
        handleLogin();
      }
      return pepApiRequest({
        method: RequestMethod.Post,
        url: generateApiPath(API_SOLUTION_SAVE_DASHBOARD, { solutionId }),
        params: {
          twinId: id,
          msalAdxToken: dashboardToken,
        },
      });
    },
    () => {
      showAlert({
        type: AlertType.Success,
        title: 'Dashboard created and saved',
        text: `Dasboard for the ${solutionName} is created successfully`,
      });
      onClose(null, ModalCloseReason.completeAction);
      actions.assetDashboard({ solutionId, twinId: id });
    },
    (err) => {
      showAlert({
        type: AlertType.Error,
        title: 'Dashboard creation Error',
        text: 'Error while creating and saving Dasboard.',
      });
    }
  );

  // const [FormComponent, setValue] = useForm({
  //   sx: formStyle,
  //   fields: [],
  //   // onSubmit: handleAnalyse,
  // });

  return (
    <ModalDialog
      title="Create new Dashboard"
      open={open}
      onClose={onClose}
      loading={loading}
      onConfirm={handleSubmit}
      confirmBtnText="Save"
      maxWidth="xl"
      fullWidth
    >
      <Grid item xs={9}>
        <iframe
          ref={iframeRef}
          title="adx example"
          src={
            !iframeLoad
              ? `https://dataexplorer.azure.com/?${iframeConfig.features}`
              : `https://dataexplorer.azure.com/clusters/${iframeConfig.cluster}.${iframeConfig.zone}/databases/${solutionId}?${iframeConfig.features}`
          }
          width="100%"
          height="700"
        />
      </Grid>
    </ModalDialog>
  );
}

export default SolutionAssetDashboardCreateContainer;
